import { useState, useEffect, useRef, useCallback } from 'react';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { directMutations } from '../utils/auth-helpers';
import { Doc, Id } from '@/convex/_generated/dataModel';
import { diagnosticFailureClass } from '@/lib/value-free-diagnostics';

// Define AuthenticatedUser type
export type AuthenticatedUser = Doc<'users'> & { id: Id<'users'> };

// Define AuthState interface
export interface AuthState {
	user: AuthenticatedUser | null;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	isLoading: boolean;
	walletAddress: string | null;
}

export const useAuth = () => {
	const {
		user: web3AuthUser,
		isLoggedIn,
		isWeb3AuthLoading,
		walletAddress,
	} = useWeb3Auth();
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState<AuthenticatedUser | null>(null);
	const [isAdmin, setIsAdmin] = useState(false);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const lastLoginUpdatedRef = useRef(false);
	const userCheckedRef = useRef(false);

	// Convex queries and mutations
	const getUserByEmail = useQuery(
		api.users.getByEmail,
		web3AuthUser?.email ? { email: web3AuthUser.email } : 'skip'
	);

	const updateLastLogin = useMutation(api.users.updateLastLogin);

	// Function to update user state based on database user
	const updateUserState = useCallback((dbUser: Doc<'users'> | null) => {
		if (dbUser) {
			setUser({
				...dbUser,
				id: dbUser._id, // Ensure id is set for compatibility
			});

			setIsAdmin(dbUser.role === 'admin' || dbUser.role === 'superadmin');
			// Check BOTH role field AND direct isSuperAdmin field
			setIsSuperAdmin(
				dbUser.role === 'superadmin' || dbUser.isSuperAdmin === true
			);
			userCheckedRef.current = true;
		} else {
			setUser(null);
			setIsAdmin(false);
			setIsSuperAdmin(false);
			userCheckedRef.current = true;
		}
	}, []);

	// Fetch user from Convex when Web3Auth user is available
	useEffect(() => {
		const syncUser = async () => {
			// Skip if still loading Convex data or no Web3Auth user
			if (getUserByEmail === undefined || !web3AuthUser?.email) {
				if (getUserByEmail === null) {
					// If query returned null explicitly (not undefined/loading)
					updateUserState(null);
					setIsLoading(false);
				}
				return;
			}

			try {
				if (isLoggedIn && web3AuthUser.email) {
					// User authenticated in Web3Auth, check Convex DB
					if (getUserByEmail) {
						// User exists in Convex DB
						updateUserState(getUserByEmail);

						// Update last login time - but only do this once per session
						if (
							getUserByEmail._id &&
							!lastLoginUpdatedRef.current
						) {
							lastLoginUpdatedRef.current = true;
							await updateLastLogin({
								userId: getUserByEmail._id,
							});
						}
					} else {
						// User doesn't exist in Convex DB
						updateUserState(null);
					}
				} else {
					// Not logged in with Web3Auth - clear user state regardless of cache
					setUser(null);
					setIsAdmin(false);
					setIsSuperAdmin(false);
					lastLoginUpdatedRef.current = false;
				}
			} catch (error) {
				console.error('Error syncing user data:', {
					failureClass: diagnosticFailureClass(error),
				});
				setUser(null);
				setIsAdmin(false);
				setIsSuperAdmin(false);
			} finally {
				setIsLoading(false);
			}
		};

		syncUser();
	}, [isLoggedIn, web3AuthUser, getUserByEmail, updateUserState, updateLastLogin]);

	// Also provide a direct function to check user
	const checkUserInDatabase = useCallback(async () => {
		if (!web3AuthUser?.email) return null;

		try {
			// Use direct mutation to avoid any caching issues
			const directResult = await directMutations.query({
				database: 'convex',
				tableName: 'users',
				queryName: 'getByEmail',
				queryArgs: { email: web3AuthUser.email },
			});

			if (directResult) {
				// Update local state to sync with direct result
				updateUserState(directResult as unknown as Doc<'users'>);
				return directResult;
			}

			// If direct lookup failed, use the already available query result if it exists
			if (getUserByEmail) {
				updateUserState(getUserByEmail);
				return getUserByEmail;
			}

			// If query result isn't available, wait for up to 2 seconds for it
			let attempts = 0;
			const maxAttempts = 4; // 4 attempts * 500ms = 2 seconds max wait

			while (attempts < maxAttempts && getUserByEmail === undefined) {
				await new Promise((resolve) => setTimeout(resolve, 500));
				attempts++;
			}

			// Check again after waiting
			if (getUserByEmail) {
				updateUserState(getUserByEmail);
				return getUserByEmail;
			}

			return null;
		} catch (error) {
			console.error('Error checking user in database:', {
				failureClass: diagnosticFailureClass(error),
			});
			return null;
		}
	}, [web3AuthUser?.email, getUserByEmail, updateUserState]);

	return {
		user,
		isAdmin,
		isSuperAdmin,
		isLoading: isLoading || isWeb3AuthLoading,
		walletAddress,
		checkUserInDatabase,
	};
};
